// packages/web/src/lib/radarCadence.ts — R3b-2: ONE place that turns a
// radar topic's cadence {every,at,day} (fleet/radar/cadence.ts's own shape
// on the Eden Box, validated there — daily/weekly/monthly, "HH:MM", day
// 1-7 ISO weekday or 1-28 day-of-month) into the words Huan reads, and
// next_due_at into a Toronto-time "next run <when>" line. Shared by
// RadarHome's topic card, RadarTopics' rows + add-a-topic form, and
// RadarTopic's in-place edit — one formula, not three copies of it.

export type CadenceEvery = 'daily' | 'weekly' | 'monthly';

export const CADENCE_OPTIONS: ReadonlyArray<{ id: CadenceEvery; label: string }> = [
  { id: 'daily', label: 'every day at 06:00' },
  { id: 'weekly', label: 'every week, Monday 06:00' },
  { id: 'monthly', label: 'every month, the 1st, 06:00' },
];

export interface CadenceShape {
  every?: string;
  at?: string;
  day?: number;
}

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

/** 'tracking · daily 06:00' / 'tracking · weekly, Mondays 06:00' /
 *  'tracking · monthly, the 1st, 06:00' — or the bare state word when
 *  there is nothing to schedule (paused/once/archived), or a plain
 *  'tracking' for a topic whose cadence was never set. */
export function cadenceLine(state: string, cadence: CadenceShape | undefined): string {
  if (state === 'paused') return 'paused';
  if (state === 'once') return 'once';
  if (state === 'archived') return 'archived';
  if (!cadence || !cadence.every) return 'tracking';
  const at = cadence.at ?? '06:00';
  if (cadence.every === 'weekly') {
    const day = WEEKDAY_NAMES[(cadence.day ?? 1) - 1] ?? 'Monday';
    return `tracking · weekly, ${day}s ${at}`;
  }
  if (cadence.every === 'monthly') return `tracking · monthly, the ${ordinal(cadence.day ?? 1)}, ${at}`;
  return `tracking · daily ${at}`;
}

/** The {every,at,day} to send for one of the three blessed cadence choices
 *  — weekly always Monday (day 1), monthly always the 1st (day 1); a
 *  weekday/day-of-month picker is not in this chunk's scope. */
export function cadenceForChoice(id: CadenceEvery): { every: CadenceEvery; at: string; day?: number } {
  if (id === 'weekly') return { every: 'weekly', at: '06:00', day: 1 };
  if (id === 'monthly') return { every: 'monthly', at: '06:00', day: 1 };
  return { every: 'daily', at: '06:00' };
}

/** 'next run Mon, 06:00' in Toronto time (America/Toronto — the box's own
 *  zone, same as fleet/radar/cadence.ts) — or 'no next run scheduled'.
 *
 *  R3g (C4, 2026-09-27): confirmed still correct once Pythia started
 *  answering `next_due_at: null` for any topic not in 'tracking' state (a
 *  `once` topic among them) — this already renders the honest, short
 *  'no next run scheduled' rather than a stale or fabricated date, and it
 *  never returns an empty string, so callers pairing it with a cadence word
 *  via ' · ' never print a dangling separator. Left unchanged. */
export function nextRunLabel(nextDueAt: string | null | undefined): string {
  if (!nextDueAt) return 'no next run scheduled';
  const when = new Date(nextDueAt).toLocaleString('en-US', {
    timeZone: 'America/Toronto',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `next run ${when}`;
}
